import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Icon, Avatar, LoadingSpinner } from '@doxyme/ui';
import { selectInstitution } from '../../../store/features/system';
import { useProviderFullNameBuilder, isLocationsAvailable, } from '../../../modules/core';
import { FilterOptions, FilterType, SectionType } from '../../common/components/filter-options';
import { AnalyticsHelpers } from '../analytics.helpers';
import getFilterOptions from './filter-block.helper';
import './filter-block.scss';
const optionsPerPage = 15;
const maxCountOfRecentlyUsedOptionsVisible = 3;
const minCountOfOptionsToShowRecentlyUsedSection = 11;
export const FilterBlock = ({ onChange, selectedProvidersIds, selectedRoomsIds, selectedLocationsIds, }) => {
    const wrapperRef = useRef();
    const intl = useIntl();
    const cn = AnalyticsHelpers.makeAnalyticsClass('filter');
    const institution = useSelector(selectInstitution);
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [availableOptions, setAvailableOptions] = useState([]);
    const [visibleOptionsCount, setVisibleOptionsCount] = useState(optionsPerPage);
    const [searchValue, setSearchValue] = useState('');
    const nameBuilder = useProviderFullNameBuilder();
    const loadOptions = async () => {
        const options = await getFilterOptions(institution.id, nameBuilder);
        setAvailableOptions(options);
    };
    const isOptionSelected = (option) => {
        switch (option.type) {
            case FilterType.All:
                return (selectedRoomsIds.length === 0 &&
                    selectedProvidersIds.length === 0 &&
                    selectedLocationsIds.length === 0);
            case FilterType.Provider:
                return selectedProvidersIds.includes(option.id);
            case FilterType.Room:
                return selectedRoomsIds.includes(option.id);
            case FilterType.Location:
                return selectedLocationsIds.includes(option.id);
            default:
                throw new Error(`Unknown option type: ${option.type}`);
        }
    };
    const toggleSelect = () => {
        // Reload all options when dropdown is opened to keep the list up to date
        if (!isDropdownOpen) {
            void loadOptions();
        }
        setDropdownOpen(!isDropdownOpen);
    };
    const onOptionSelect = (item) => {
        toggleSelect();
        const providersIds = [];
        const roomsIds = [];
        const locationIds = [];
        if (item.type !== FilterType.All) {
            AnalyticsHelpers.addRecentlyUsedOption(item, maxCountOfRecentlyUsedOptionsVisible + 1);
        }
        if (onChange) {
            switch (item.type) {
                case FilterType.All:
                    break;
                case FilterType.Room:
                    roomsIds.push(item.id);
                    break;
                case FilterType.Provider:
                    providersIds.push(item.id);
                    break;
                case FilterType.Location:
                    locationIds.push(item.id);
                    break;
                default:
                    throw new Error(`Unknown option type: ${item.type}`);
            }
            onChange({ providersIds, roomsIds, locationIds });
        }
    };
    const handleClickOutside = (event) => {
        if (!wrapperRef.current.contains(event.target)) {
            setDropdownOpen(false);
        }
    };
    const handleInfiniteScrollEndReach = () => {
        if (visibleOptionsCount < availableOptions.length) {
            setVisibleOptionsCount(visibleOptionsCount + optionsPerPage);
        }
    };
    const getOptions = (limit = 0, searchString = '') => {
        const searchStringLC = searchString.toLowerCase();
        const count = availableOptions.length;
        const sections = [];
        const recentOptionsFiltered = AnalyticsHelpers.getRecentlyUsedOptions()
            .map(option => availableOptions.find(item => item.type === option.type && item.id === option.id))
            .filter(option => (option !== undefined))
            .filter(option => !isOptionSelected(option)) // Hide currently selected option from recently used list
            .slice(0, maxCountOfRecentlyUsedOptionsVisible)
            .filter(option => option.searchByString.includes(searchStringLC))
            .map(option => ({
            ...option,
            reactKey: `recent-${option.type}-${option.id}`
        }));
        const availableOptionsFiltered = availableOptions.filter(option => option.searchByString.includes(searchStringLC));
        const locationsCount = availableOptionsFiltered.filter((option) => option.type === FilterType.Location).length;
        const isAllSectionVisible = searchStringLC === '';
        const isRecentlyUsedSectionVisible = count >= minCountOfOptionsToShowRecentlyUsedSection &&
            recentOptionsFiltered.length > 0;
        const isMainSectionVisible = availableOptionsFiltered.length > 0;
        const isLocationVisible = isLocationsAvailable() && locationsCount > 0;
        if (isAllSectionVisible) {
            sections.push({
                type: SectionType.All,
                showDividerAfter: !isRecentlyUsedSectionVisible &&
                    isMainSectionVisible &&
                    !isLocationVisible,
                options: [
                    {
                        reactKey: 'all',
                        type: FilterType.All,
                        searchByString: '',
                        optionLabel: intl.formatMessage({ id: 'account.analytics.filter.all' }, { count }),
                    }
                ]
            });
        }
        if (isRecentlyUsedSectionVisible) {
            const recentOptionsSorted = (searchStringLC === '')
                ? recentOptionsFiltered
                : recentOptionsFiltered.sort((option1, option2) => option1.sortByString.localeCompare(option2.sortByString));
            sections.push({
                type: SectionType.Recent,
                label: intl.formatMessage({ id: 'account.analytics.filter.recently-viewed' }),
                showDividerAfter: isMainSectionVisible && !isLocationVisible,
                options: recentOptionsSorted
            });
        }
        if (isLocationVisible) {
            const locationsOptions = availableOptionsFiltered
                .filter((option) => option.type === FilterType.Location)
                .sort((option1, option2) => option1.sortByString.localeCompare(option2.sortByString));
            sections.push({
                type: SectionType.Location,
                label: intl.formatMessage({ id: 'account.analytics.filter.locations' }),
                showDividerAfter: isMainSectionVisible,
                options: locationsOptions,
            });
        }
        if (isMainSectionVisible) {
            const mainSectionOptions = (limit > 0) ? availableOptionsFiltered.slice(0, limit) : availableOptionsFiltered;
            sections.push({
                type: SectionType.Main,
                options: isLocationVisible
                    ? mainSectionOptions.filter((option) => option.type !== FilterType.Location)
                    : mainSectionOptions,
            });
        }
        return sections;
    };
    const getSelectedOption = () => {
        const sections = getOptions();
        for (const section of sections) {
            const foundOption = section.options.find(option => isOptionSelected(option));
            if (foundOption) {
                return foundOption;
            }
        }
        return null;
    };
    useEffect(() => {
        void loadOptions();
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    const selectedOption = getSelectedOption();
    const visibleSections = getOptions(visibleOptionsCount, searchValue);
    // Switch to "All" if currently selected option is not available anymore
    useEffect(() => {
        if (availableOptions.length > 0 && !selectedOption) {
            onChange({ providersIds: [], roomsIds: [], locationIds: [] });
        }
    });
    if (availableOptions.length === 0 || !selectedOption) {
        return (<div className={'loader-block'}>
        <LoadingSpinner size='x-small'/>
      </div>);
    }
    return (<div className={cn('select-wrapper')} ref={wrapperRef}>
      <div className={cn('label-wrapper')} onClick={toggleSelect}>
        {selectedOption.imageUrl &&
            <Avatar className={cn('avatar')} src={selectedOption.imageUrl}/>}
        {!selectedOption.imageUrl &&
            selectedOption.type === FilterType.All &&
            <Avatar className={cn(['avatar', 'avatar_generic-clinic'])} iconName="clinic-generic-picture"/>}
        {!selectedOption.imageUrl &&
            selectedOption.type === FilterType.Provider &&
            <Avatar className={cn(['avatar', 'avatar_generic-user'])} iconName="user"/>}
        {!selectedOption.imageUrl &&
            selectedOption.type === FilterType.Room &&
            <Avatar className={cn(['avatar', 'avatar_generic-waiting-room'])} iconName="waiting-room"/>}
        {!selectedOption.imageUrl &&
            selectedOption.type === FilterType.Location && (<Avatar className={cn(['avatar', 'avatar_generic-location'])} iconName="location"/>)}
        <h3 className={cn('label-text')} data-test-id='meeting-history-entire-clinic-btn'>{selectedOption.selectedOptionLabel ?? selectedOption.optionLabel}</h3>
        {isDropdownOpen ? <Icon className={cn('arrow')} name='chevron-up' size='x-small'/> : <Icon className={cn('arrow')} name='chevron-down' size='x-small'/>}
      </div>
      {isDropdownOpen
            &&
                <FilterOptions className={cn('dropdown')} visibleSections={visibleSections} searchValue={searchValue} setSearchValue={setSearchValue} isOptionSelected={isOptionSelected} onOptionSelect={onOptionSelect} handleInfiniteScrollEndReach={handleInfiniteScrollEndReach} isOnCall={false}/>}
    </div>);
};
